import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useServices } from './ServicesContext'
import useServicesResources from '../shared/resources/api/v1/admin/docker/useServicesResources'
import useServiceVariablesState from '../hooks/services/useServiceVariablesState'
import useServiceVolumesState from '../hooks/services/useServiceVolumesState'
import useServicePortMappingsState from '../hooks/services/useServicePortMappingsState'
import useServiceDependenciesState from '../hooks/services/useServiceDependenciesState'
import useServiceLogsState from '../hooks/services/useServiceLogsState'
import useServiceLabelsState from '../hooks/services/useServiceLabelsState'
import useServiceIngressesState from '../hooks/services/useServiceIngressesState'
import useServiceNetworksState from '../hooks/services/useServiceNetworksState'
import useServiceCronJobsState from '../hooks/services/useServiceCronJobsState'

const ServiceContext = createContext(null)

export function ServiceProvider({ children }) {
  const { teamSlug, projectSlug, serviceSlug } = useParams()
  const { reload: reloadServices } = useServices()
  const { get, update, destroy } = useServicesResources(teamSlug, projectSlug)
  const [service, setService] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const loadedIdRef = useRef(null)
  const serviceRef = useRef(service)
  serviceRef.current = service

  // Sub-resource hooks
  const variablesState = useServiceVariablesState(teamSlug, projectSlug, serviceSlug)
  const volumesState = useServiceVolumesState(teamSlug, projectSlug, serviceSlug)
  const portMappingsState = useServicePortMappingsState(teamSlug, projectSlug, serviceSlug)
  const dependenciesState = useServiceDependenciesState(teamSlug, projectSlug, serviceSlug, service?.id)
  const logsState = useServiceLogsState(teamSlug, projectSlug, serviceSlug)
  const labelsState = useServiceLabelsState(teamSlug, projectSlug, serviceSlug)
  const ingressesState = useServiceIngressesState(teamSlug, projectSlug, serviceSlug)
  const networksState = useServiceNetworksState(teamSlug, projectSlug, serviceSlug, service?.projectId)
  const cronJobsState = useServiceCronJobsState(teamSlug, projectSlug, serviceSlug)

  const loadService = useCallback(() => {
    if (!serviceSlug) return Promise.resolve(null)
    if (loadedIdRef.current === serviceSlug && serviceRef.current) {
      return Promise.resolve(serviceRef.current)
    }

    setLoading(true)
    setError(null)
    return get(serviceSlug)
      .then((response) => {
        setService(response.service)
        loadedIdRef.current = serviceSlug
        return response.service
      })
      .catch((err) => {
        setError(err)
        throw err
      })
      .finally(() => {
        setLoading(false)
      })
  }, [serviceSlug, get])

  const updateService = useCallback((id, data) => {
    return update(id, data)
      .then((response) => {
        setService(response.service)
        return response.service
      })
  }, [update])

  const destroyService = useCallback((id) => {
    return destroy(id).then(() => reloadServices())
  }, [destroy, reloadServices])

  useEffect(() => {
    loadService()
  }, [serviceSlug])

  // Load sub-resources when service loads
  useEffect(() => {
    if (!loading && service) {
      variablesState.load()
      volumesState.load()
      portMappingsState.load()
      dependenciesState.load()
      labelsState.load()
      ingressesState.load()
      networksState.load()
      cronJobsState.load()
    }
  }, [loading, service])

  const value = useMemo(() => ({
    // Service
    service,
    loading,
    error,
    reload: loadService,
    updateService,
    destroyService,

    // Variables
    variables: variablesState.variables,
    variablesLoading: variablesState.loading,
    createVariable: variablesState.createVariable,
    updateVariable: variablesState.updateVariable,
    destroyVariable: variablesState.destroyVariable,

    // Volumes
    volumes: volumesState.volumes,
    volumesLoading: volumesState.loading,
    createVolume: volumesState.createVolume,
    updateVolume: volumesState.updateVolume,
    destroyVolume: volumesState.destroyVolume,

    // Port Mappings
    portMappings: portMappingsState.portMappings,
    portMappingsLoading: portMappingsState.loading,
    createPortMapping: portMappingsState.createPortMapping,
    updatePortMapping: portMappingsState.updatePortMapping,
    destroyPortMapping: portMappingsState.destroyPortMapping,

    // Dependencies
    dependencies: dependenciesState.dependencies,
    dependenciesLoading: dependenciesState.loading,
    availableServices: dependenciesState.availableServices,
    createDependency: dependenciesState.createDependency,
    destroyDependency: dependenciesState.destroyDependency,

    // Logs
    logs: logsState.logs,
    logsLoading: logsState.loading,
    logsMeta: logsState.meta,
    loadLogs: logsState.load,
    refreshLogs: logsState.refresh,

    // Labels
    labels: labelsState.labels,
    labelsLoading: labelsState.loading,
    createLabel: labelsState.createLabel,
    updateLabel: labelsState.updateLabel,
    destroyLabel: labelsState.destroyLabel,

    // Ingresses
    ingresses: ingressesState.ingresses,
    ingressesLoading: ingressesState.loading,
    createIngress: ingressesState.createIngress,
    updateIngress: ingressesState.updateIngress,
    destroyIngress: ingressesState.destroyIngress,

    // Networks
    serviceNetworks: networksState.serviceNetworks,
    serviceNetworksLoading: networksState.loading,
    availableNetworks: networksState.availableNetworks,
    createServiceNetwork: networksState.createServiceNetwork,
    destroyServiceNetwork: networksState.destroyServiceNetwork,

    // Cron Jobs
    cronJobs: cronJobsState.cronJobs,
    cronJobsLoading: cronJobsState.loading,
    createCronJob: cronJobsState.createCronJob,
    updateCronJob: cronJobsState.updateCronJob,
    destroyCronJob: cronJobsState.destroyCronJob,
  }), [
    service, loading, error, loadService, updateService, destroyService,
    variablesState, volumesState, portMappingsState, dependenciesState,
    logsState, labelsState, ingressesState, networksState, cronJobsState,
  ])

  return (
    <ServiceContext.Provider value={value}>
      {children}
    </ServiceContext.Provider>
  )
}

export function useService() {
  const context = useContext(ServiceContext)
  if (!context) {
    throw new Error('useService must be used within a ServiceProvider')
  }
  return context
}
